import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        { email }
      );
      setSent(true);
      if (data.dev_reset_link) {
        setDevLink(data.dev_reset_link);
        toast.info('Email service not configured - showing link directly');
      } else {
        toast.success('Check your email for a reset link');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6 paper-texture">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BookOpen className="w-8 h-8 text-[#6B7A4F]" />
            <span className="text-2xl font-bold text-[#2D2A26]">Bookish Bestie</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26] mb-2">
            Forgot <span className="font-bold">Password?</span>
          </h1>
          <p className="text-[#5C5955]">Enter your email and we'll send you a reset link</p>
        </div>

        <div className="glass-card p-8 rounded-2xl space-y-6">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#E2E6D5] rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-[#6B7A4F]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2D2A26]">Check your inbox</h3>
              <p className="text-[#5C5955]">
                If an account exists with <span className="font-semibold">{email}</span>, we've sent a password reset link.
              </p>
              {devLink && (
                <div className="bg-[#F4F1EA] p-3 rounded-lg text-xs break-all">
                  <p className="text-[#5C5955] mb-1 font-semibold">Dev mode link:</p>
                  <a href={devLink} className="text-[#6B7A4F] underline">{devLink}</a>
                </div>
              )}
              <Link to="/login" className="inline-block text-[#6B7A4F] font-semibold hover:underline">
                ← Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email"
                  className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] rounded-xl"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit"
                className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white py-6 rounded-full font-semibold"
              >
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</> : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-[#5C5955] hover:text-[#6B7A4F]">
                  Remember your password? Sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
